import { Check, Receipt, Wallet as WalletIcon } from 'lucide-react';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  CardSubtitle,
  CardTitle,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Select,
  Skeleton,
} from '@/components/ui';
import { useNotify } from '@/hooks/useNotify';
import { useI18n, type TranslationKey } from '@/i18n';
import { api, type AdminUser, type LedgerEntry, type Wallet } from '@/lib/api';
import { formatDateTime, formatPrice } from '@/lib/format';

const MODE_LABEL = {
  PREPAID: 'billing.modePREPAID',
  INVOICE: 'billing.modeINVOICE',
} as const satisfies Record<'PREPAID' | 'INVOICE', TranslationKey>;

/** Betrag in Euro als Text -> ganzzahlige Cent, Vorzeichen bleibt erhalten. */
function toCents(value: string): number {
  const parsed = Number.parseFloat(value.replace(',', '.'));
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
}

export function AdminBillingPage() {
  const { t } = useI18n();
  const notify = useNotify();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [selected, setSelected] = useState('');
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [billingMode, setBillingMode] = useState<'PREPAID' | 'INVOICE'>('PREPAID');
  const [loading, setLoading] = useState(true);

  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const [creditLimit, setCreditLimit] = useState('');
  const [lowBalance, setLowBalance] = useState('');

  useEffect(() => {
    void (async () => {
      try {
        const data = await api.admin.users();
        setUsers(data.users);
        setSelected(data.users[0]?.id ?? '');
      } catch (cause) {
        notify.fromError(cause, t('errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    })();
  }, [notify, t]);

  const loadWallet = useCallback(
    async (userId: string) => {
      if (!userId) return;
      try {
        const data = await api.adminBilling.wallet(userId);
        setWallet(data.wallet);
        setEntries(data.entries);
        setBillingMode(data.user.billingMode);
        setCreditLimit((data.wallet.creditLimitCents / 100).toFixed(2));
        setLowBalance((data.wallet.lowBalanceCents / 100).toFixed(2));
      } catch (cause) {
        notify.fromError(cause, t('errors.loadFailed'));
      }
    },
    [notify, t],
  );

  useEffect(() => {
    void loadWallet(selected);
  }, [selected, loadWallet]);

  const book = async (event: FormEvent) => {
    event.preventDefault();
    const cents = toCents(amount);
    if (cents === 0 || note.trim().length < 3) return;

    setBusy(true);
    try {
      await api.adminBilling.adjust(selected, cents, note.trim());
      notify.success(t('billing.adjustBooked'));
      setAmount('');
      setNote('');
      await loadWallet(selected);
    } catch (cause) {
      notify.fromError(cause, t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  const saveLimits = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.adminBilling.setLimits(selected, {
        creditLimitCents: Math.max(0, toCents(creditLimit)),
        lowBalanceCents: toCents(lowBalance),
      });
      notify.success(t('billing.limitsSaved'));
      await loadWallet(selected);
    } catch (cause) {
      notify.fromError(cause, t('errors.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const changeMode = async (mode: 'PREPAID' | 'INVOICE') => {
    setBusy(true);
    try {
      await api.adminBilling.setBillingMode(selected, mode);
      setBillingMode(mode);
      notify.success(t('common.save'));
    } catch (cause) {
      notify.fromError(cause, t('errors.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Skeleton className="h-64" />;

  return (
    <div>
      <PageHeader title={t('billing.adminTitle')} subtitle={t('billing.adminSubtitle')} />

      <Card className="mb-4">
        <FormField label={t('billing.selectUser')}>
          <Select value={selected} onChange={(event) => setSelected(event.target.value)}>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.email}
                {user.name ? ` — ${user.name}` : ''}
              </option>
            ))}
          </Select>
        </FormField>
      </Card>

      {wallet && (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,340px)_1fr]">
          <div className="space-y-4">
            <Card>
              <div className="mb-3 flex items-center gap-2">
                <WalletIcon size={16} className="text-t2" />
                <CardTitle>{t('billing.balance')}</CardTitle>
              </div>
              <p
                className={`text-[26px] font-extrabold tabular-nums ${
                  wallet.balanceCents < 0 ? 'text-bad' : 'text-t0'
                }`}
              >
                {formatPrice(wallet.balanceCents, wallet.currency)}
              </p>
              <p className="mt-1 text-[12px] text-t2">
                {t('billing.available')}: {formatPrice(wallet.availableCents, wallet.currency)}
              </p>

              <div className="mt-4 flex gap-2">
                {(['PREPAID', 'INVOICE'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    disabled={busy}
                    onClick={() => void changeMode(mode)}
                    className={`flex-1 rounded-[var(--radius-vela-sm)] border px-3 py-2 text-xs font-semibold transition ${
                      billingMode === mode
                        ? 'border-acc bg-acc-soft text-acc'
                        : 'border-line bg-bg-3 text-t1 hover:border-line-2'
                    }`}
                  >
                    {t(MODE_LABEL[mode])}
                  </button>
                ))}
              </div>
            </Card>

            <Card>
              <CardHeader>
                <div>
                  <CardTitle>{t('billing.adjustTitle')}</CardTitle>
                  <CardSubtitle>{t('billing.adjustNoteHint')}</CardSubtitle>
                </div>
              </CardHeader>
              <form onSubmit={book} className="space-y-3">
                <FormField label={t('billing.adjustAmount')} required>
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="10,00"
                    value={amount}
                    onChange={(event) => setAmount(event.target.value)}
                  />
                </FormField>
                <FormField label={t('billing.adjustNote')} required>
                  <Input value={note} onChange={(event) => setNote(event.target.value)} />
                </FormField>
                <Button
                  type="submit"
                  fullWidth
                  disabled={busy || toCents(amount) === 0 || note.trim().length < 3}
                >
                  {t('common.save')}
                </Button>
              </form>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('billing.limitsTitle')}</CardTitle>
              </CardHeader>
              <form onSubmit={saveLimits} className="space-y-3">
                <FormField label={t('billing.creditLimit')}>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={creditLimit}
                    onChange={(event) => setCreditLimit(event.target.value)}
                  />
                </FormField>
                <FormField label={t('billing.lowBalanceThreshold')} hint={t('billing.lowBalanceHint')}>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={lowBalance}
                    onChange={(event) => setLowBalance(event.target.value)}
                  />
                </FormField>
                <Button type="submit" fullWidth variant="secondary" disabled={busy}>
                  {t('common.save')}
                </Button>
              </form>
            </Card>
          </div>

          <div className="space-y-4">
            <Card padding="none">
              <div className="border-b border-line px-5 py-4">
                <CardTitle>{t('billing.history')}</CardTitle>
              </div>
              {entries.length === 0 ? (
                <div className="p-6">
                  <EmptyState icon="💶" title={t('billing.historyEmpty')} />
                </div>
              ) : (
                <div className="max-h-[420px] overflow-auto">
                  <table className="w-full text-[13px]">
                    <tbody className="divide-y divide-line">
                      {entries.map((entry) => (
                        <tr key={entry.id}>
                          <td className="whitespace-nowrap px-5 py-3 text-t2">
                            {formatDateTime(entry.createdAt)}
                          </td>
                          <td className="px-5 py-3">
                            <span className="text-t0">{entry.description}</span>
                            {entry.note && (
                              <p className="mt-0.5 text-[12px] text-t2">{entry.note}</p>
                            )}
                          </td>
                          <td
                            className={`whitespace-nowrap px-5 py-3 text-right font-semibold tabular-nums ${
                              entry.amountCents > 0 ? 'text-ok' : 'text-t0'
                            }`}
                          >
                            {entry.amountCents > 0 ? '+' : ''}
                            {formatPrice(entry.amountCents, wallet.currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            <AdminInvoiceList />
          </div>
        </div>
      )}
    </div>
  );
}

/** Alle Rechnungen aller Kunden - Ausstellen und Zahlungseingang buchen. */
function AdminInvoiceList() {
  const { t } = useI18n();
  const notify = useNotify();
  const [invoices, setInvoices] = useState<
    Awaited<ReturnType<typeof api.adminBilling.invoices>>['invoices']
  >([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api.adminBilling.invoices();
      setInvoices(data.invoices);
    } catch (cause) {
      notify.fromError(cause, t('errors.loadFailed'));
    }
  }, [notify, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (id: string, action: 'issue' | 'paid') => {
    setBusy(true);
    try {
      if (action === 'issue') {
        const result = await api.adminBilling.issue(id);
        notify.success(t('billing.invoiceIssued', { number: result.number }));
      } else {
        await api.adminBilling.markPaid(id);
        notify.success(t('billing.invoiceMarkedPaid'));
      }
      await load();
    } catch (cause) {
      notify.fromError(cause, t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card padding="none">
      <div className="flex items-center gap-2 border-b border-line px-5 py-4">
        <Receipt size={16} className="text-t2" />
        <CardTitle>{t('billing.invoicesTitle')}</CardTitle>
      </div>

      {invoices.length === 0 ? (
        <div className="p-6">
          <EmptyState icon="🧾" title={t('billing.invoicesEmpty')} />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <tbody className="divide-y divide-line">
              {invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td className="whitespace-nowrap px-5 py-3 font-semibold text-t0">
                    {invoice.status === 'DRAFT' ? '—' : invoice.number}
                  </td>
                  <td className="px-5 py-3 text-t1">{invoice.user.email}</td>
                  <td className="px-5 py-3">
                    <Badge
                      variant={
                        invoice.status === 'PAID'
                          ? 'success'
                          : invoice.status === 'OPEN'
                            ? 'warning'
                            : 'neutral'
                      }
                    >
                      {invoice.status}
                    </Badge>
                  </td>
                  <td className="whitespace-nowrap px-5 py-3 text-right tabular-nums text-t0">
                    {formatPrice(invoice.grossCents, invoice.currency)}
                  </td>
                  <td className="whitespace-nowrap px-5 py-3 text-right">
                    {invoice.status === 'DRAFT' && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => void act(invoice.id, 'issue')}
                      >
                        {t('billing.issueInvoice')}
                      </Button>
                    )}
                    {invoice.status === 'OPEN' && (
                      <Button
                        size="sm"
                        variant="outline"
                        icon={<Check size={14} />}
                        disabled={busy}
                        onClick={() => void act(invoice.id, 'paid')}
                      >
                        {t('billing.markPaid')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
